//CORE
import React from "react";
import Audit from '../../core/components/Audit.js';
import ListOfValues from '../../core/components/DataManagement/DataManagementDetailListOfValues.js';
import Locales from '../../core/components/DataManagement/DataManagementDetailLocales.js';
import AppMenuItems from '../../core/components/AppMenu/AppMenuItems.js';
import AppMenuItemRoutes from '../../core/components/AppMenu/AppMenuItemRoutes.js';
import AppMenuItemRouteTabs from '../../core/components/AppMenu/AppMenuItemRouteTabs.js';
// Caveats/Scorecard/Metrics/CycleFrames/Faults/PlanItems/NextFilming were
// previously registered here as analysis_report tabs (grid list + popup per
// entity) - all of that content now lives exclusively on the "Full Report"
// page (analysis_requests_analysis_report_full_report below), which replaced
// tab-hopping with one editable page covering every chapter. Those tab
// components' source files are unused now but left in place rather than
// deleted, in case anyone still wants the old grid-per-entity view back.
import AnalysisReportCycleFrameImage from '../components/AnalysisReport/AnalysisReportCycleFrameImage.js';
import AnalysisReportVariableDrills from '../components/AnalysisReport/AnalysisReportVariableDrills.js';
import AnalysisReportVariableImage from '../components/AnalysisReport/AnalysisReportVariableImage.js';
import AnalysisReportAiUsageList from '../components/AnalysisReport/AnalysisReportAiUsageList.js';
import AccountAnalysisRequests from '../components/Account/AccountAnalysisRequests.js';
import ConsumerThemeProvider,{isConsumer} from '../components/ConsumerTheme.js';

// Consumer screens render under the Lane4 consumer theme (ConsumerTheme.js).
// The provider is a no-op for staff, so shared routes like my_user stay as-is for them.
const consumerPage=(Page)=>(props)=>(
  <ConsumerThemeProvider userData={props.userData}>
    <Page {...props}/>
  </ConsumerThemeProvider>
);

// /my_notifications is also where staff land from the header bell - they keep
// the core grid, consumers get the card feed.
const CoreNotifications=React.lazy(()=>import('../../core/components/Notification/Notifications.js'));
const ConsumerNotifications=consumerPage(React.lazy(()=>import('../components/Notification/Consumer/MyNotifications.js')));
const MyNotificationsByRole=(props)=>isConsumer(props.userData)?<ConsumerNotifications {...props}/>:<CoreNotifications {...props}/>;

const EntityComponentMap={
  accounts_accounts:React.lazy(()=>import('../../core/components/Account/Accounts.js')),
  accounts_account:React.lazy(()=>import('../components/Account/AccountDetail.js')),
  accounts_account_new:React.lazy(()=>import('../../core/components/Account/AccountNew.js')),
  analysis_guidelines_analysis_guidelines:React.lazy(()=>import('../components/AnalysisGuideline/AnalysisGuidelines.js')),
  analysis_guidelines_analysis_guideline:React.lazy(()=>import('../components/AnalysisGuideline/AnalysisGuidelineDetail.js')),
  analysis_guidelines_analysis_guideline_new:React.lazy(()=>import('../components/AnalysisGuideline/AnalysisGuidelineNew.js')),
  analysis_requests_analysis_requests:React.lazy(()=>import('../components/AnalysisRequest/AnalysisRequests.js')),
  analysis_requests_analysis_request:React.lazy(()=>import('../components/AnalysisRequest/AnalysisRequestDetail.js')),
  analysis_requests_analysis_request_new:React.lazy(()=>import('../components/AnalysisRequest/AnalysisRequestNew.js')),
  analysis_requests_analysis_report:React.lazy(()=>import('../components/AnalysisReport/AnalysisReportDetail.js')),
  analysis_requests_analysis_report_full_report:React.lazy(()=>import('../components/AnalysisReport/FullEditor/AnalysisReportFullReportPage.js')),
  analysis_requests_analysis_report_preview:React.lazy(()=>import('../components/AnalysisReport/AnalysisReportPreview.js')),
  analysis_requests_analysis_report_cycle_frame_new:React.lazy(()=>import('../components/AnalysisReport/AnalysisReportCycleFrameNew.js')),
  analysis_requests_analysis_report_cycle_frame:React.lazy(()=>import('../components/AnalysisReport/AnalysisReportCycleFrameDetail.js')),
  analysis_requests_analysis_report_variable:React.lazy(()=>import('../components/AnalysisReport/AnalysisReportVariableDetail.js')),
  my_analyses_my_analyses:consumerPage(React.lazy(()=>import('../components/AnalysisRequest/Consumer/MyAnalyses.js'))),
  my_analyses_my_analysis:consumerPage(React.lazy(()=>import('../components/AnalysisRequest/Consumer/MyAnalysisDetail.js'))),
  my_analyses_my_analysis_new:consumerPage(React.lazy(()=>import('../components/AnalysisRequest/Consumer/MyAnalysisNew.js'))),
  my_analyses_my_analysis_report:consumerPage(React.lazy(()=>import('../components/AnalysisRequest/Consumer/MyAnalysisReport.js'))),
  notifications_notifications:React.lazy(()=>import('../../core/components/Notification/Notifications.js')),
  notifications_notification:React.lazy(()=>import('../../core/components/Notification/Notification.js')),
  my_notifications_my_notifications:MyNotificationsByRole,
  my_notifications_my_notification:consumerPage(React.lazy(()=>import('../../core/components/Notification/Notification.js'))),
  users_users:React.lazy(()=>import('../../core/components/User/Users.js')),
  users_user:React.lazy(()=>import('../components/User/UserDetail.js')),
  users_user_new:React.lazy(()=>import('../../core/components/User/UserDetailNew.js')),
  my_user_my_user:consumerPage(React.lazy(()=>import('../components/User/MyUserDetail.js'))),
  error_logs_error_logs:React.lazy(()=>import('../../core/components/ErrorLog/ErrorLogs.js')),
  server_management_server_management:React.lazy(()=>import('../../core/components/ServerManagement/ServerManagementDetail.js')),
  app_roles_app_roles:React.lazy(()=>import('../../core/components/AppRole/AppRoles.js')),
  data_management_data_management:React.lazy(()=>import('../../core/components/DataManagement/DataManagementDetail.js')),
  app_menus_app_menus:React.lazy(()=>import('../../core/components/AppMenu/AppMenus.js')),
  app_menus_app_menu:React.lazy(()=>import('../../core/components/AppMenu/AppMenuDetail.js')),
  app_menus_app_menu_item:React.lazy(()=>import('../../core/components/AppMenu/AppMenuItemDetail.js')),
  app_menus_app_menu_item_route:React.lazy(()=>import('../../core/components/AppMenu/AppMenuItemRouteDetail.js')),
};

export const getEntityComponent=(entity)=>EntityComponentMap[entity]||null;

const PageTabComponentMap={
    account:{
      analysis_requests:AccountAnalysisRequests,
      audit:Audit,
    },
    analysis_request:{
      audit:Audit,
    },
    analysis_guideline:{
      audit:Audit,
    },
    analysis_report:{
      ai_usage:AnalysisReportAiUsageList,
      audit:Audit,
    },
    analysis_report_cycle_frame:{
      image:AnalysisReportCycleFrameImage,
      audit:Audit,
    },
    analysis_report_variable:{
      drills:AnalysisReportVariableDrills,
      image:AnalysisReportVariableImage,
      audit:Audit,
    },
    user:{
      audit:Audit,
    },
    data_management:{
      list_of_values:ListOfValues,
      locales:Locales,
    },
    app_menu:{
      items:AppMenuItems,
      audit:Audit,
    },
    app_menu_item:{
      routes:AppMenuItemRoutes,
      audit:Audit,
    },
    app_menu_item_route:{
      route_tabs:AppMenuItemRouteTabs,
      audit:Audit,
    },
};

export const getPageTabComponent=(entityKey,tabKey)=>{
  const entityMap=PageTabComponentMap[entityKey];
  if(!entityMap) return null;
  return entityMap[tabKey]||null;
};
